import React, { useMemo, useState } from 'react';
import {
  Button, Col, Collapse, Container, Row,
} from 'react-bootstrap';
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineCheckCircle,
  HiOutlineLockClosed,
  HiOutlineShieldCheck,
} from 'react-icons/hi';
import { IoArrowForward } from 'react-icons/io5';
import logo from 'assets/img/logo-blue-full.png';
import Form from 'components/Form/Layout';
import TextField from 'components/Form/FormTextField';
import { LEGAL_NOTICE_URL } from 'constants/routes';
import validateForm from 'utils/Validator';
import { yupLoginSsoIdentifier, yupLoginSsoOtpCode } from 'utils/yupFields';

export enum STEPS {
  REQUEST_OTP = 'REQUEST_OTP',
  VERIFY_OTP = 'VERIFY_OTP',
}

function maskEmail( email: string ) {
  const at = email.indexOf( '@' );
  if ( at <= 0 ) return email;
  const local = email.slice( 0, at );
  const domain = email.slice( at + 1 );
  const head = local.slice( 0, Math.min( 2, local.length ) );
  return `${head}***@${domain}`;
}

type LoginSsoViewProps = {
  step: STEPS;
  identifier: string;
  submitError?: string;
  requestOtp: ( values: Record<string, any> ) => Promise<void>;
  verifyOtp: ( values: Record<string, any> ) => Promise<void>;
  onChangeEmail: () => void;
};

function LoginSsoView( {
  step,
  identifier,
  submitError,
  requestOtp,
  verifyOtp,
  onChangeEmail,
}: LoginSsoViewProps ) {
  const requestOtpValidation = useMemo( () => validateForm( yupLoginSsoIdentifier ), [] );
  const verifyOtpValidation = useMemo( () => validateForm( yupLoginSsoOtpCode ), [] );

  const otpDestination = useMemo(
    () => ( identifier ? maskEmail( identifier ) : '' ),
    [identifier],
  );

  const [showWhyNoPassword, setShowWhyNoPassword] = useState( false );

  return (
    <Container fluid className="px-4 px-md-0 body-login-container">
      <Row className="vh-100 g-0">
        <Col className="body-login-bg d-none d-lg-block" />
        <Col xs={12} md={9} lg={6} className="m-auto card--light py-5 px-3 px-lg-5">
          <div className="text-center mb-4 d-none d-md-block">
            <img src={logo} height={55} alt="Logo" />
          </div>
          <div className="text-center mb-5 d-md-none">
            <img src={logo} height={40} alt="Logo" />
          </div>

          {step === STEPS.REQUEST_OTP && (
            <Form
              initialValues={{ identifier: '' }}
              validate={requestOtpValidation}
              onSubmit={requestOtp}
              body={( form: any ) => (
                <Row>
                  <Col md={12} className="m-auto">
                    <h3 className="h5 mt-3 mb-5 fw-semibold">
                      Introduzca su correo y le enviaremos un código de un solo uso.
                    </h3>

                    <TextField field="identifier" label="Email" />

                    <p className="mt-3 mb-0 small">
                      Al continuar aceptas la
                      {' '}
                      <a
                        href={LEGAL_NOTICE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-decoration-underline fw-bold"
                      >
                        Política de Privacidad
                      </a>
                      .
                    </p>

                    <hr className="my-4 pb-1" />

                    <button
                      type="button"
                      className="text-primary text-decoration-none text-start w-100 p-0 mb-2 d-flex align-items-start justify-content-between gap-2 border-0 bg-transparent shadow-none"
                      onClick={() => setShowWhyNoPassword( ( open ) => !open )}
                      aria-expanded={showWhyNoPassword}
                      aria-controls="login-sso-why-no-password"
                      id="login-sso-why-no-password-toggle"
                    >
                      <span className="flex-grow-1">
                        ¿Por qué hemos eliminado la contraseña y pedimos el email?
                      </span>
                      {showWhyNoPassword ? (
                        <HiChevronUp size={22} className="flex-shrink-0 mt-0" aria-hidden />
                      ) : (
                        <HiChevronDown size={22} className="flex-shrink-0 mt-0" aria-hidden />
                      )}
                    </button>

                    <Collapse in={showWhyNoPassword}>
                      <div id="login-sso-why-no-password" className="mt-4">
                        <div className="d-flex align-items-center mb-3">
                          <HiOutlineShieldCheck size={22} />
                          <p className="mb-0 ms-2">
                            Para
                            {' '}
                            <span className="text--bold">mitigar riesgos</span>
                            {' '}
                            en materia de fraude.
                          </p>
                        </div>

                        <div className="d-flex align-items-center mb-3">
                          <HiOutlineCheckCircle size={22} />
                          <p className="mb-0 ms-2">
                            Para que pueda
                            {' '}
                            <span className="text--bold">comenzar de inmediato</span>
                            {' '}
                            y usar nuestros servicios de Impar Capital.
                          </p>
                        </div>

                        <div className="d-flex align-items-center mb-3">
                          <HiOutlineLockClosed size={26} />
                          <p className="mb-0 ms-2">
                            Para
                            {' '}
                            <span className="text--bold">verificarlo</span>
                            {' '}
                            durante el inicio de sesión mediante autenticación con una contraseña
                            de un sólo uso (OTP).
                          </p>
                        </div>
                      </div>
                    </Collapse>

                    {submitError && (
                      <div className="invalid-feedback d-block mt-3">{submitError}</div>
                    )}

                    <Row className="mt-4">
                      <Col xs={12}>
                        <Button
                          variant="primary"
                          type="submit"
                          size="sm"
                          className="w-100 btn--rounded"
                          disabled={form.getState().hasValidationErrors}
                        >
                          Siguiente
                          <IoArrowForward size={20} className="ms-2" />
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
            />
          )}

          {step === STEPS.VERIFY_OTP && (
            <Form
              initialValues={{ otpCode: '' }}
              validate={verifyOtpValidation}
              onSubmit={verifyOtp}
              body={( form: any ) => (
                <Row>
                  <Col md={12} className="m-auto">
                    <h3 className="text-center mb-3">Verifica tu correo electrónico</h3>

                    <div className="d-flex justify-content-center align-items-center mb-4">
                      <p className="text-start mb-0">
                        {otpDestination
                          ? `Te hemos enviado un código de 6 caracteres a ${otpDestination}. Revisa tu bandeja de entrada y spam.`
                          : 'Introduce el código de verificación'}
                      </p>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-decoration-underline py-0 fw-bold px-2"
                        onClick={onChangeEmail}
                      >
                        Cambiar
                      </Button>
                    </div>

                    <p className="text--bold mb-2">Introduce el código de verificación</p>
                    <TextField
                      field="otpCode"
                      style={{ textTransform: 'uppercase' }}
                    />

                    <p className="text--sm text--medium mb-1">El código OTP sólo será válido por 5 minutos.</p>

                    {submitError && (
                      <div className="invalid-feedback d-block mt-3">{submitError}</div>
                    )}

                    <Button
                      variant="primary"
                      type="submit"
                      size="sm"
                      className="w-100 btn--rounded mt-4"
                      disabled={form.getState().hasValidationErrors}
                    >
                      Verificar
                      <IoArrowForward size={20} className="ms-2" />
                    </Button>
                  </Col>
                </Row>
              )}
            />
          )}
        </Col>
        <Col className="d-none d-lg-block">
          <div className="body-login-bg body-login-bg-right w-100 h-100" />
        </Col>
      </Row>
    </Container>
  );
}

export default LoginSsoView;
